import type {
  MarketBoardHtsApiResponse,
  MarketBoardQuotesApiResponse,
} from '@app-types/summaryApi';
import type {
  IndexInstrumentViewModel,
  IndicesChartState,
  IndicesCountryViewModel,
  IndicesFormattedNumber,
  IndicesQuoteViewModel,
} from '@app-types/indices';

const DECIMAL_PATTERN = /^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/;
const DATE_PATTERN = /^\d{8}$/;
const UPDATED_AT_PATTERN = /^(\d{4})\/(\d{2})\/(\d{2})(?: (\d{2}):(\d{2}):(\d{2}))?$/;
const MISSING_UPDATED_AT = '更新: --/--/--';
const UNKNOWN_COUNTRY = 'unknown';
const UNKNOWN_COUNTRY_LABEL = 'その他';

export const normalizeMarketBoardDecimalPlaces = (value: unknown): number | null => (
  typeof value === 'number'
  && Number.isInteger(value)
  && value >= 0
  && value <= 20
    ? value
    : null
);

const groupInteger = (value: string): string => {
  const sign = value.startsWith('+') || value.startsWith('-') ? value[0] : '';
  const unsigned = sign ? value.slice(1) : value;
  return `${sign}${unsigned.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
};

const formatValidatedDecimal = (value: string): string => {
  const dotIndex = value.indexOf('.');
  if (dotIndex < 0) return groupInteger(value);
  const integer = value.slice(0, dotIndex);
  const fraction = value.slice(dotIndex + 1);
  return `${groupInteger(integer)}.${fraction}`;
};

const formatExactDecimal = (value: number, decimalPlaces: number): string => (
  new Intl.NumberFormat('en-US', {
    useGrouping: true,
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  }).format(value)
);

const getFractionLength = (value: string): number => {
  const fraction = value.split('.')[1];
  return fraction?.length ?? 0;
};

const toPlainNumberString = (value: number): string => {
  const source = String(value);
  if (!/[eE]/.test(source)) return source;

  const [coefficient, exponentText] = source.toLowerCase().split('e');
  const exponent = Number(exponentText);
  const sign = coefficient.startsWith('-') ? '-' : '';
  const unsigned = coefficient.replace(/^[+-]/, '');
  const [integer, fraction = ''] = unsigned.split('.');
  const digits = `${integer}${fraction}`;
  const decimalIndex = integer.length + exponent;

  if (decimalIndex <= 0) {
    return `${sign}0.${'0'.repeat(-decimalIndex)}${digits}`;
  }
  if (decimalIndex >= digits.length) {
    return `${sign}${digits}${'0'.repeat(decimalIndex - digits.length)}`;
  }
  return `${sign}${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`;
};

export const formatMarketBoardNumber = (
  raw: number | string | null | undefined,
  decimalPlaces?: number | null,
): IndicesFormattedNumber => {
  const normalizedDecimalPlaces = normalizeMarketBoardDecimalPlaces(decimalPlaces);

  if (typeof raw === 'number') {
    if (!Number.isFinite(raw)) {
      return { raw, numericValue: null, text: '--', isUsable: false, decimalPlaces: 0 };
    }
    const source = toPlainNumberString(raw);
    return {
      raw,
      numericValue: raw,
      text: normalizedDecimalPlaces === null
        ? formatValidatedDecimal(source)
        : formatExactDecimal(raw, normalizedDecimalPlaces),
      isUsable: true,
      decimalPlaces: normalizedDecimalPlaces ?? getFractionLength(source),
    };
  }

  if (typeof raw !== 'string') {
    return { raw, numericValue: null, text: '--', isUsable: false, decimalPlaces: 0 };
  }

  const source = raw.trim();
  if (!source || !DECIMAL_PATTERN.test(source)) {
    return { raw, numericValue: null, text: '--', isUsable: false, decimalPlaces: 0 };
  }

  const numericValue = Number(source);
  if (!Number.isFinite(numericValue)) {
    return { raw, numericValue: null, text: '--', isUsable: false, decimalPlaces: 0 };
  }

  return {
    raw,
    numericValue,
    text: normalizedDecimalPlaces === null
      ? formatValidatedDecimal(source)
      : formatExactDecimal(numericValue, normalizedDecimalPlaces),
    isUsable: true,
    decimalPlaces: normalizedDecimalPlaces ?? getFractionLength(source),
  };
};

export const formatMarketBoardPrice = (
  raw: number | string | null | undefined,
  decimalPlaces?: number | null,
): IndicesFormattedNumber => {
  const formatted = formatMarketBoardNumber(raw, decimalPlaces);
  return formatted.numericValue === 0
    ? { ...formatted, numericValue: null, text: '--', isUsable: false }
    : formatted;
};

export const formatMarketBoardUpdatedAt = (raw: unknown): string => {
  if (typeof raw !== 'string') return MISSING_UPDATED_AT;
  const source = raw.trim();
  const match = UPDATED_AT_PATTERN.exec(source);
  if (!match) return MISSING_UPDATED_AT;

  const [, yearText, monthText, dayText, hourText, minuteText, secondText] = match;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const isLeapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const daysInMonth = [31, isLeapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const validDate = year > 0 && month >= 1 && month <= 12
    && day >= 1 && day <= daysInMonth[month - 1];
  const validTime = hourText === undefined || (
    Number(hourText) <= 23
    && Number(minuteText) <= 59
    && Number(secondText) <= 59
  );

  return validDate && validTime ? `更新: ${source}` : MISSING_UPDATED_AT;
};

const toTrimmedText = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
};

const getChangeTone = (
  value: number | null,
): IndicesQuoteViewModel['changeTone'] => (
  value === null || value === 0 ? 'neutral' : value > 0 ? 'positive' : 'negative'
);

export const mapMarketBoardQuotes = (
  response: MarketBoardQuotesApiResponse | undefined,
): IndicesQuoteViewModel[] => {
  if (response?.status !== 0 || !Array.isArray(response.data)) return [];

  return response.data.reduce<IndicesQuoteViewModel[]>((result, row, sourceIndex) => {
    if (!row || typeof row !== 'object') return result;
    const ric = toTrimmedText(row.ric);
    if (!ric) return result;

    const apiDecimalPlaces = normalizeMarketBoardDecimalPlaces(row.dp);
    const close = formatMarketBoardPrice(row.closePrice, apiDecimalPlaces);
    const change = formatMarketBoardPrice(row.netChange, apiDecimalPlaces);
    const high = formatMarketBoardPrice(row.high, apiDecimalPlaces);
    const low = formatMarketBoardPrice(row.low, apiDecimalPlaces);
    const country = toTrimmedText(row.country) ?? UNKNOWN_COUNTRY;

    result.push({
      id: `${ric}::${sourceIndex}`,
      sourceIndex,
      ric,
      marketType: toTrimmedText(row.marketType),
      country,
      countryLabel: toTrimmedText(row.countryLabel) ?? UNKNOWN_COUNTRY_LABEL,
      name: toTrimmedText(row.label) ?? ric,
      updatedAtText: formatMarketBoardUpdatedAt(row.dateTime),
      valueText: close.text,
      changeText: change.text,
      changeTone: getChangeTone(change.numericValue),
      highText: high.text,
      lowText: low.text,
      apiDecimalPlaces,
      quoteDecimalPlaces: close.isUsable ? close.decimalPlaces : null,
    });
    return result;
  }, []);
};

export const buildOrderedHtsRics = (
  rows: readonly Pick<IndicesQuoteViewModel, 'ric'>[],
): string[] => {
  const seen = new Set<string>();
  const ordered: string[] = [];
  rows.forEach(({ ric }) => {
    if (!seen.has(ric)) {
      seen.add(ric);
      ordered.push(ric);
    }
  });
  return ordered;
};

const isValidCsvNumber = (value: string): boolean => {
  if (!DECIMAL_PATTERN.test(value)) return false;
  return Number.isFinite(Number(value));
};

const normalizeHtsRow = (line: string): string | null => {
  const columns = line.split(',').map((column) => column.trim());
  if (columns.length !== 7 || !DATE_PATTERN.test(columns[0])) return null;
  if (!columns.slice(1).every(isValidCsvNumber)) return null;

  const open = Number(columns[1]);
  const high = Number(columns[2]);
  const low = Number(columns[3]);
  const close = Number(columns[4]);
  if (
    high < Math.max(open, close)
    || low > Math.min(open, close)
    || high < low
  ) return null;

  return columns.join(',');
};

export const normalizeMarketBoardHtsCsv = (
  csv: string | null | undefined,
): string | null => {
  if (typeof csv !== 'string' || !csv.trim()) return null;
  const validRows = csv
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map(normalizeHtsRow)
    .filter((row): row is string => row !== null);
  return validRows.length > 0 ? validRows.join('\n') : null;
};

export const mapMarketBoardHtsByRic = (
  response: MarketBoardHtsApiResponse | undefined,
): ReadonlyMap<string, string> => {
  const result = new Map<string, string>();
  if (response?.status !== 0 || !Array.isArray(response.data)) return result;

  response.data.forEach((item) => {
    const ric = toTrimmedText(item?.ric);
    if (!ric || result.has(ric) || item?.response?.status !== 0) return;
    const normalized = normalizeMarketBoardHtsCsv(item.response.data);
    if (normalized) result.set(ric, normalized);
  });
  return result;
};

const getCsvOhlcDecimalPlaces = (csv: string): number => (
  csv.split('\n').reduce((maximum, row) => {
    const columns = row.split(',');
    return Math.max(
      maximum,
      ...columns.slice(1, 5).map(getFractionLength),
    );
  }, 0)
);

export const inferIndicesDecimalPlaces = (
  quote: Pick<IndicesQuoteViewModel, 'apiDecimalPlaces' | 'quoteDecimalPlaces'>,
  normalizedCsv: string | null,
): number => {
  if (quote.apiDecimalPlaces !== null) return quote.apiDecimalPlaces;
  if (quote.quoteDecimalPlaces !== null) return quote.quoteDecimalPlaces;
  return normalizedCsv ? getCsvOhlcDecimalPlaces(normalizedCsv) : 0;
};

export const joinIndexQuotesWithHts = (
  quotes: readonly IndicesQuoteViewModel[],
  htsByRic: ReadonlyMap<string, string>,
  queryState: 'loading' | 'success' | 'error',
): IndexInstrumentViewModel[] => quotes.map((quote) => {
  const chartData = queryState === 'success' ? htsByRic.get(quote.ric) ?? null : null;
  const chartState: IndicesChartState = queryState === 'loading'
    ? 'loading'
    : queryState === 'error'
      ? 'error'
      : chartData
        ? 'ready'
        : 'no-data';

  return {
    ...quote,
    chartData,
    chartState,
    decimalPlaces: inferIndicesDecimalPlaces(quote, chartData),
  };
});

export const groupIndexQuotesByCountry = (
  rows: readonly IndexInstrumentViewModel[],
): IndicesCountryViewModel[] => {
  const groups = new Map<string, IndicesCountryViewModel>();
  rows.forEach((instrument) => {
    const existing = groups.get(instrument.country);
    if (existing) {
      (existing.instruments as IndexInstrumentViewModel[]).push(instrument);
      return;
    }
    groups.set(instrument.country, {
      country: instrument.country,
      countryLabel: instrument.countryLabel,
      instruments: [instrument],
    });
  });
  return Array.from(groups.values());
};
